import {FormBuilderAnswerableFileUpload, FormBuilderQuestion, User} from "@/types";
import {ExclamationCircleIcon} from '@heroicons/react/20/solid'
import React, {useEffect, useMemo, useState} from "react";
import {useDropzone} from 'react-dropzone';
import {usePage} from "@inertiajs/react";

interface Props {
    question: FormBuilderQuestion,
    hasError: boolean
}

export default function AnswerableFileUpload<T extends { answerable: FormBuilderAnswerableFileUpload }>({
                                                                                                            question,
                                                                                                            data,
                                                                                                            setData,
                                                                                                            hasError,
                                                                                                            deleteFiles,
                                                                                                            officeId,
                                                                                                            settings
                                                                                                        }: Props) {
    // Sometimes when you add a file to the input[type=file] elements, and refresh the page, it keeps the FileList
    // in cache. This bugs out the system, because we can't run .filter(...) on a FileList.
    // Instead, when we fresh we just show an empty file input
    const questionDataNotIncludingFileList = data[question.id] && typeof data[question.id][0] === 'string' ? data[question.id] : []
    console.log('questionDataNotIncludingFileList', questionDataNotIncludingFileList)
    const [previouslyUploadedFiles, setPreviouslyUploadedFiles] = useState(questionDataNotIncludingFileList)


    // check if the default is null and convert it to a string because React doesnt like null
    const fileUploaders = !data[question.id] ? [] : data[question.id];

    useEffect(() => {
        // We do this because when you the press 'next' button on the form, and the new page has a file upload
        // It will be blank due to 'previouslyUploadedFiles' being empty from before
        // Without this, it would not refresh the file list and stay empty, even though there could be an existing file
        // uploaded, e.g. from user details or from going back and then forward again.
        setPreviouslyUploadedFiles(questionDataNotIncludingFileList)
    }, [questionDataNotIncludingFileList])

    return (
        <div>
            {previouslyUploadedFiles.filter((f) => f !== null).length > 0
                ? listExistingFiles(previouslyUploadedFiles, deleteFiles, question, data, setData, setPreviouslyUploadedFiles, officeId, settings)
                : <FileUploaderHtml question={question} setData={setData}/>}

            {(hasError) ? (errorBadge()) : (<></>)}
        </div>
    );
}

function errorBadge() {
    return (<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
    </div>)
}

const fileToMime = {
    'png': {mime: 'image/png', extensions: ['.png']},
    'jpg': {mime: 'image/jpeg', extensions: ['.jpg', '.jpeg']},
    'pdf': {mime: 'application/pdf', extensions: ['.pdf']},
    'html': {mime: 'text/html', extensions: ['.html', '.htm']},
    'txt': {mime: 'text/plain', extensions: ['.txt']},
    'doc': {mime: 'application/msword', extensions: ['.doc']},
    'docx': {
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        extensions: ['.docx']
    },
    'csv': {mime: 'text/csv', extensions: ['.csv']},
    'xls': {mime: 'application/vnd.ms-excel', extensions: ['.xls']},
};
const fileToMimeRegexExpression = new RegExp(Object.keys(fileToMime).map((key) => `${fileToMime[key].mime},`).join('|'), 'g');


function FileUploaderHtml({question, setData}) {
    console.log(question.answerable.mimes)
    let accept = {};

    question.answerable.mimes.split(',')
        .filter((e) => fileToMime[e])
        .forEach((e) => {
            const {mime, extensions} = fileToMime[e];
            accept[mime] = extensions;
        })

    if (Object.keys(accept).length === 0) {
        accept = {'*': []}
    }

    const {
        acceptedFiles,
        fileRejections,
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject
    } = useDropzone({
        accept: accept,
        maxFiles: question.answerable.max_selected ?? 0,
        maxSize: question.answerable.max_size ?? Infinity,
        minSize: question.answerable.min_size ?? 0,
        onDrop: files => {
            console.log('files', files)
            console.log('howdy')
            setData(question.id, files.map(f => Object.assign(f, {preview: URL.createObjectURL(f)})))
        }
    });
    const baseStyle = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        borderWidth: 1,
        borderRadius: 5,
        borderColor: '#D1D5DB',
        borderStyle: 'dashed',
        backgroundColor: 'transparent',
        color: '#80858E',
        outline: 'none',
        transition: 'border .24s ease-in-out'
    };

    const focusedStyle = {
        borderColor: '#2196f3'
    };

    const acceptStyle = {
        borderColor: '#00e676'
    };

    const rejectStyle = {
        borderColor: '#ff1744'
    };
    const style = useMemo(() => ({
        ...baseStyle,
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [
        isFocused,
        isDragAccept,
        isDragReject
    ]);
    const files = acceptedFiles.map(file => (
        <li key={file.path} className={'rounded p-2 bg-gray-50 border border-gray-100 mb-2 ml-2 truncate max-w-xs'}>
            <img src={file.preview}
                 alt={file.path}
                 className={'h-32 w-32 object-cover rounded'}
                 onLoad={() => {
                     URL.revokeObjectURL(file.preview)
                 }}/>
        </li>))
    const fileRejectionItems = fileRejections.map(({file, errors}) => (
        <li key={file.path} className={'truncate'}>
            {file.path} - {(file.size / 1024).toFixed(2)}kb
            {errors.map(e => (
                <span key={e.code}
                      className="text-red-600 text-sm ml-2 truncate max-w-xs">{e.message.replace(fileToMimeRegexExpression, ' ')}</span>
            ))}
        </li>
    ));

    return (
        <section className="container">
            <div {...getRootProps({className: 'dropzone', style})}>
                <input
                    {...getInputProps()}
                />
                <p className="text-sm"><span
                    className="text-[#1F9C8A] font-semibold cursor-pointer">Upload a file</span> or drag and drop</p>
            </div>
            <aside className="text-sm">
                {files.length > 0 && <div>
                    <div className={'font-medium text-sm ml-2 mt-2'}>Attachments</div>
                    <ul className={'flex flex-wrap text-gray-700 -ml-2'}>{files}</ul>
                </div>}
                {fileRejectionItems.length > 0 && <div>
                    <h4 className="font-semibold">Rejected files</h4>
                    <ul>{fileRejectionItems}</ul>
                </div>}
            </aside>
        </section>
    );
}

function listExistingFiles(previouslyUploadedFiles, deleteFiles, question, data, setData, setPreviouslyUploadedFiles, officeId, settings) {

    const {auth} = usePage().props as unknown as {
        auth: { user: User | null },
    }
    return (
        <div>
            {previouslyUploadedFiles.length > 0 && (<div>
                <div className={'font-medium text-sm ml-2'}>Attachments</div>
                <ul className={'flex flex-wrap text-gray-700 -ml-2'}>{previouslyUploadedFiles.map((f, i) => {
                    return (<li key={i}
                                className={'rounded px-4 py-2 bg-gray-50 border border-gray-100 mb-2 ml-2 break-all'}>{auth.user ?
                        <a href={route('lead-uploads.show', {filename: f})}
                           target={"_blank"}>{f.split('/').pop()}</a> : <>{f.split('/').pop()}</>}</li>)
                })}
                </ul>
            </div>)}
            <button className={`bg-[${settings.primaryColour}] text-[${settings.secondaryColour}] p-2 rounded`} type="button"
                    onClick={() => {
                        deleteFiles(question, data, setData, officeId);
                        setPreviouslyUploadedFiles([null])
                    }}>Re-upload files
            </button>
        </div>);
}
